import React, { createContext, useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import Notification from '../components/Notification';
import { getApiBaseUrl } from '../config';

/**
 * @typedef {Object} Advertisement
 * @property {string} id
 * @property {string} name
 * @property {boolean} displayLogo
 * @property {string} displayLogoRef
 * @property {number} displayLogoX
 * @property {number} displayLogoY
 * @property {boolean} displayMediaBar
 * @property {number} displayMediaBarX
 * @property {number} displayMediaBarY
 * @property {string} scheduledStartDate
 * @property {number} displayTimeInMilliseconds
 * @property {boolean} active
 * @property {string} creditCondition
 * @property {number} startHourCondition
 * @property {number} startMinuteCondition
 * @property {number} endHourCondition
 * @property {number} endMinuteCondition
 * @property {number} priority
 * @property {string} imageRef
 * @property {boolean} displayErZijnNogCreditsCard
 * @property {number} erZijnNogCreditsCardX
 * @property {number} erZijnNogCreditsCardY
 * @property {number} artworkX
 * @property {number} artworkY
 * @property {number} artworkWidth
 * @property {number} artworkHeight
 * @property {number} freeTextReference1X
 * @property {number} freeTextReference1Y
 * @property {number} freeTextReference1FontSize
 * @property {number} freeTextReference2X
 * @property {number} freeTextReference2Y
 * @property {number} freeTextReference2FontSize
 * @property {string[]} [subscribedMachines] - Machine IDs subscribed to this ad (from detail request)
 */

export const AdsContext = createContext({
  /** @type {Advertisement[]} */
  advertisements: [],
  loading: false,
  error: null,
  fetchAdvertisements: async () => {},
  fetchAdvertisementResources: async () => {},
  createAdvertisement: async () => {},
  updateAdvertisement: async () => {},
  addAdToMachine: async () => {},
  deleteAdFromMachine: async () => {},
  showSuccessNotification: () => {},
  showErrorNotification: () => {},
});

const AdsProvider = ({ children }) => {
  const { authData } = useContext(AuthContext);
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);

  const addNotification = (message, type) => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, message, type }]);
  };

  const showErrorNotification = (message) => {
    addNotification(message, 'error');
  };

  const showSuccessNotification = (message) => {
    addNotification(message, 'success');
  };

  const fetchAdvertisements = async () => {
    if (!authData?.accessToken) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const baseUrl = getApiBaseUrl();
      const all = [];
      let page = 0;
      const size = 100;

      while (true) {
        const response = await fetch(
          `${baseUrl}/advertisements/?page=${page}&size=${size}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch advertisements');
        }

        const data = await response.json();
        const list = Array.isArray(data) ? data : (data.content ?? data.data ?? []);
        if (!list || list.length === 0) break;

        all.push(...list);
        if (list.length < size) break;
        page += 1;
      }

      // Enrich each ad with subscribedMachines from advertisements/{id}
      const enriched = await Promise.all(
        all.map(async (ad) => {
          try {
            const detailRes = await fetch(
              `${baseUrl}/advertisements/${ad.id}`,
              {
                method: 'GET',
                headers: {
                  Authorization: `Bearer ${authData.accessToken}`,
                  'Content-Type': 'application/json',
                },
              }
            );
            if (!detailRes.ok) {
              return { ...ad, subscribedMachines: [] };
            }
            const detail = await detailRes.json();
            const subscribedMachines =
              detail.SubscribedMachines ??
              detail.subscribedMachines ??
              [];
            return { ...ad, subscribedMachines };
          } catch {
            return { ...ad, subscribedMachines: [] };
          }
        })
      );

      setAdvertisements(enriched);
    } catch (err) {
      setError(err.message);
      showErrorNotification(`Error fetching advertisements: ${err.message}`);
      setAdvertisements([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * POST /advertisements – create a new ad. Only the name is passed; all other fields use defaults.
   * @param {string} name – display name for the ad
   * @returns {Promise<Advertisement|undefined>} created ad or undefined on failure
   */
  const createAdvertisement = async (name) => {
    if (!authData?.accessToken) return;
    const baseUrl = getApiBaseUrl();
    const payload = {
      name: name ?? '',
      displayLogo: true,
      displayLogoRef: 'dj-logo-spinner.png',
      displayLogoX: 1170,
      displayLogoY: 50,
      displayMediaBar: false,
      displayMediaBarX: 50,
      displayMediaBarY: 780,
      scheduledStartDate: '2026-01-01',
      displayTimeInMilliseconds: 12000,
      active: true,
      creditCondition: 'NONE',
      startHourCondition: 0,
      startMinuteCondition: 0,
      endHourCondition: 0,
      endMinuteCondition: 0,
      priority: 10000,
      imageRef: 'screensaver-bonus-background.png',
      displayErZijnNogCreditsCard: false,
      erZijnNogCreditsCardX: 0,
      erZijnNogCreditsCardY: 0,
      artworkReference: 0,
      artworkX: 175,
      artworkY: 425,
      artworkWidth: 550,
      artworkHeight: 550,
      freeTextReference1X: 0,
      freeTextReference1Y: 0,
      freeTextReference1FontSize: 0,
      freeTextReference2X: 0,
      freeTextReference2Y: 0,
      freeTextReference2FontSize: 0,
    };
    try {
      const response = await fetch(`${baseUrl}/advertisements/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authData.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Failed to create advertisement');
      const created = await response.json();
      setAdvertisements((prev) => [...prev, { ...created, subscribedMachines: [] }]);
      showSuccessNotification('Advertisement created.');
      return created;
    } catch (err) {
      showErrorNotification(err.message ?? 'Failed to create advertisement');
      throw err;
    }
  };

  /**
   * GET /advertisements/resources – returns a list of strings (image paths).
   * @returns {Promise<string[]>}
   */
  const fetchAdvertisementResources = async () => {
    if (!authData?.accessToken) return [];
    const baseUrl = getApiBaseUrl();
    try {
      const response = await fetch(`${baseUrl}/advertisements/resources`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authData.accessToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch advertisement resources');
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (err) {
      showErrorNotification(err.message ?? 'Failed to fetch advertisement resources');
      return [];
    }
  };

  /**
   * PUT /advertisements/{id} – update an ad with all fetched data, excluding subscribedMachines.
   * @param {Advertisement} ad – full ad object (e.g. from context)
   */
  const updateAdvertisement = async (ad) => {
    if (!authData?.accessToken || !ad?.id) return;
    const baseUrl = getApiBaseUrl();
    const { subscribedMachines, SubscribedMachines, ...payload } = ad;
    try {
      const response = await fetch(`${baseUrl}/advertisements/${ad.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${authData.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Failed to update advertisement');
      setAdvertisements((prev) =>
        prev.map((a) => (a.id === ad.id ? { ...a, ...payload, subscribedMachines: a.subscribedMachines } : a))
      );
      showSuccessNotification('Advertisement updated.');
    } catch (err) {
      showErrorNotification(err.message ?? 'Failed to update advertisement');
      throw err;
    }
  };

  // DJB-214: `notify: false` lets the Ads & Screensaver Subscriptions bulk
  // run show a progress bar and one aggregated toast instead of one per machine
  const addAdToMachine = async (adId, machineId, { notify = true } = {}) => {
    if (!authData?.accessToken) return;
    const baseUrl = getApiBaseUrl();
    try {
      const response = await fetch(
        `${baseUrl}/advertisements/${adId}/machine/${machineId}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) throw new Error('Failed to add ad to machine');
      setAdvertisements((prev) =>
        prev.map((ad) =>
          ad.id === adId
            ? {
                ...ad,
                subscribedMachines: [
                  ...(ad.subscribedMachines ?? []),
                  machineId,
                ],
              }
            : ad
        )
      );
      if (notify) showSuccessNotification('Ad assigned to machine.');
    } catch (err) {
      if (notify) showErrorNotification(err.message ?? 'Failed to add ad to machine');
      throw err;
    }
  };

  const deleteAdFromMachine = async (adId, machineId, { notify = true } = {}) => {
    if (!authData?.accessToken) return;
    const baseUrl = getApiBaseUrl();
    try {
      const response = await fetch(
        `${baseUrl}/advertisements/${adId}/machine/${machineId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) throw new Error('Failed to remove ad from machine');
      setAdvertisements((prev) =>
        prev.map((ad) =>
          ad.id === adId
            ? {
                ...ad,
                subscribedMachines: (ad.subscribedMachines ?? []).filter(
                  (id) => id !== machineId
                ),
              }
            : ad
        )
      );
      if (notify) showSuccessNotification('Ad removed from machine.');
    } catch (err) {
      if (notify) showErrorNotification(err.message ?? 'Failed to remove ad from machine');
      throw err;
    }
  };

  useEffect(() => {
    fetchAdvertisements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData?.accessToken]);

  return (
    <AdsContext.Provider
      value={{
        advertisements,
        loading,
        error,
        fetchAdvertisements,
        fetchAdvertisementResources,
        createAdvertisement,
        updateAdvertisement,
        addAdToMachine,
        deleteAdFromMachine,
        // DJB-214: aggregated result toast for bulk subscription runs
        showSuccessNotification,
        showErrorNotification,
      }}
    >
      {children}
      <div className="notification-container">
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            onClose={() =>
              setNotifications((prev) =>
                prev.filter((n) => n.id !== notification.id)
              )
            }
          />
        ))}
      </div>
    </AdsContext.Provider>
  );
};

export default AdsProvider;
